import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { PLAN_IMAGE_LIMITS } from '@/constants';
import { Subscription } from '@/types';

interface CreditsContextType {
  subscription: Subscription | null;
  loading: boolean;
  refetch: () => Promise<void>;
  imagesUsed: number;
  imageLimit: number;
  planName: string;
  canGenerate: () => boolean;
  getUsagePercentage: () => number;
  getRemainingImages: () => number;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

const CREDITS_SAFETY_TIMEOUT_MS = 4000;

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  // Safety timeout: never keep loading=true forever
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn('[useCredits] Safety timeout (4s) — forcing loading=false');
          return false;
        }
        return prev;
      });
    }, CREDITS_SAFETY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('subscriptions' as any)
        .select('*')
        .eq('user_id', user.id)
        .limit(1);

      if (error) {
        console.error('[useCredits] fetchSubscription error:', error.code, error.message);
        throw error;
      }
      if (data && (data as any[]).length > 0) {
        setSubscription((data as any[])[0] as unknown as Subscription);
      } else {
        console.log('[useCredits] No subscription found for user', user.id);
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Realtime subscription
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`subscription-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            const newData = payload.new as unknown as Subscription;
            setSubscription(newData);
            console.log('Realtime: subscription updated', newData.images_used, '/', newData.image_limit);
          } else if (payload.eventType === 'DELETE') {
            setSubscription(null);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const imagesUsed = subscription?.images_used ?? 0;
  const imageLimit = subscription?.image_limit ?? 0;
  const planName = subscription?.plan_name ?? 'free';

  const canGenerate = useCallback((): boolean => {
    if (!subscription) return false;
    return subscription.images_used < subscription.image_limit;
  }, [subscription]);


  const getUsagePercentage = useCallback((): number => {
    if (!subscription || subscription.image_limit === 0) return 100;
    return Math.round((subscription.images_used / subscription.image_limit) * 100);
  }, [subscription]);

  const getRemainingImages = useCallback((): number => {
    if (!subscription) return 0;
    return Math.max(0, subscription.image_limit - subscription.images_used);
  }, [subscription]);

  return (
    <CreditsContext.Provider value={{
      subscription,
      loading,
      refetch: fetchSubscription,
      imagesUsed,
      imageLimit,
      planName,
      canGenerate,
      getUsagePercentage,
      getRemainingImages,
    }}>
      {children}
    </CreditsContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditsProvider');
  }
  return context;
}
